import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Appointment, EligibilityCheck, WaitListEntry } from 'src/models/appointment/_types';
import { checkAppointmentEligibility, deleteWaitListEntry } from 'src/models/appointment/appointment';
import { mergeCheckinBlockerUsers } from 'src/models/checkin/mergeCheckinBlockerUsers';
import { getUserDisplayName } from 'src/models/user/utils';
import { Visit, VisitStatus } from 'src/models/visit/_types';
import { updateVisit } from 'src/models/visit/visit';
import {
  CheckinBlockerReasonType,
  CheckinBlockerUser,
} from 'src/shared/CheckinBlocker/_types';
import toast from 'src/utils/toast';

export const useAppointmentMembersSection = () => {
  const queryClient = useQueryClient();
  const [showCheckInBlockerDialog, setShowCheckInBlockerDialog] = useState<{
    open: boolean;
    users: CheckinBlockerUser[];
  }>({
    open: false,
    users: [],
  });
  const { t: tCheckinBlocker } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });
  const updateVisitMutation = useMutation({
    mutationFn: (visit: Visit) => {
      return updateVisit(visit);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Visit(s) successfully updated');
    },
    onError: () => {
      toast.error('Visit could not be updated');
    },
  });

  const bulkUpdateVisitMutation = useMutation({
    mutationFn: (visit: Visit) => {
      return updateVisit(visit);
    },
  });

  const checkEligibilityForCompletion = async (
    visit: Visit
  ): Promise<boolean> => {
    try {
      const eligibility = await checkAppointmentEligibility({
        appointment: { id: visit.appointmentId! } as Appointment,
        user: visit.user!,
      });

      if (!eligibility.eligible && (eligibility.reason || eligibility.trainerReason)) {
        setShowCheckInBlockerDialog({
          open: true,
          users: [
            {
              id: visit.userId?.toString() ?? '',
              name: getUserDisplayName(visit.user),
              profileImage: visit.user?.profileImage,
              blockers: [
                {
                  type: (eligibility.reason || eligibility.trainerReason) as CheckinBlockerReasonType,
                  title: tCheckinBlocker('blockingReasons.custom.title') ?? '',
                  message: eligibility.reasonMessage,
                },
              ],
            },
          ],
        });
        return true;
      }
        setShowCheckInBlockerDialog({
          open: true,
          users: [
            {
              id: visit.userId?.toString() ?? '',
              name: getUserDisplayName(visit.user),
              profileImage: visit.user?.profileImage,
              blockers: [
                {
                  type: (eligibility.reason || eligibility.trainerReason) as CheckinBlockerReasonType,
                  title: tCheckinBlocker('blockingReasons.custom.title') ?? '',
                  message: eligibility.reasonMessage,
                },
              ],
            },
          ],
        });
        return true;
      } catch {
        toast.error(tCheckinBlocker('eligibilityCheckFailed'));
        return true;
      }
    return false;
  };

  const updateVisitStatus = async (
    visit: Visit,
    status: VisitStatus,
    additionalParams?: {
      chargeNoshow?: boolean;
      chargeCancellation?: boolean;
      paidByPackageId?: boolean;
    },
    overridePaymentBlock = false,
  ) => {
    if (status === VisitStatus.Completed && !overridePaymentBlock) {
      const blocked = await checkEligibilityForCompletion(visit);
      if (blocked) return;
    }

    await updateVisitMutation.mutateAsync({ ...visit, status, ...additionalParams });
  };

  const deleteWaitListMutation = useMutation({
    mutationFn: (entry: WaitListEntry) => {
      return deleteWaitListEntry({ id: entry.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Successfully removed from waitlist');
    },
    onError: () => {
      toast.error('Unable to remove from waitlist');
    },
  });

  const deleteWaitlistEntry = (entry: WaitListEntry) => {
    deleteWaitListMutation.mutate(entry);
  };

  const bulkUpdateVisits = async (visits: Visit[], params, overrideCheckInBlocker = false) => {

    const ineligibleVisits: { visit: Visit; eligibility: EligibilityCheck }[] = [];
    let eligibilityRequestFailed = false;

    const visitPromises = visits.map(async (visit) => {
      if (params.status === VisitStatus.Completed && !overrideCheckInBlocker) {
        try {
          const eligibility = await checkAppointmentEligibility({
            appointment: { id: visit.appointmentId! } as Appointment,
            user: visit.user!,
          });
          if (eligibility.reason || eligibility.trainerReason) {
            ineligibleVisits.push({ visit, eligibility });
          }
        } catch {
          eligibilityRequestFailed = true;
        }
        return;
      }
      return await bulkUpdateVisitMutation.mutateAsync({ ...visit, ...params });
    });

    try {
      await Promise.all(visitPromises);
    } catch {
      toast.error('Visit could not be updated');
      return;
    }

    if (eligibilityRequestFailed) {
      toast.error(tCheckinBlocker('eligibilityCheckFailed'));
      return;
    }

    if(ineligibleVisits.length > 0 && !overrideCheckInBlocker) {
      setShowCheckInBlockerDialog({
        open: true,
        users: mergeCheckinBlockerUsers(
          ineligibleVisits.map(({ visit, eligibility }) => ({
            id: visit.userId?.toString() ?? '',
            name: getUserDisplayName(visit.user),
            profileImage: visit.user?.profileImage,
            blockers: [
              {
                type: (eligibility.reason || eligibility.trainerReason) as CheckinBlockerReasonType,
                title: tCheckinBlocker('blockingReasons.custom.title') ?? '',
                message: eligibility.reasonMessage,
              },
            ],
          })),
        ),
      });
    } else {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Visit(s) successfully updated');
    }
  };

  return {
    updateVisitStatus,
    bulkUpdateVisits,
    deleteWaitlistEntry,
    isUpdatingVisit: updateVisitMutation.isPending,
    showCheckInBlockerDialog,
    setShowCheckInBlockerDialog,
  };
};
